// @flow

import * as React from "react";
import { withRouter, Redirect } from "react-router-dom";
import NProgress from "nprogress";
import { ReactForm, ReactFormField } from "@adrenalin/react-form";
import queryString from "query-string";

import API from "../api/";
import useAsyncState from "../hooks/useAsyncState";

import HeroContent from "../components/organisms/HeroContent";
import Loader from "../components/atoms/Loader";
import Button from "../components/atoms/Button";
import ServerErrorMessage from "../components/atoms/ServerErrorMessage";
import RichTextContent from "../components/molecules/RichTextContent";

type PropsType = {
    page: any,
};

const EventTerms = (props: PropsType): React.Node => {
    const [state, localActions] = useAsyncState();
    const queryParamProps = queryString.parse(props.history.location.search);
    const [pageError, setPageError] = React.useState(null);
    const [eventData, setEventData] = React.useState(null);
    const [agreeSuccess, setAgreeSuccess] = React.useState(false);
    const successRef = React.useRef(null);

    React.useEffect(() => {
        fetchEventTerms();
    }, []);

    const fetchEventTerms = () => {
        NProgress.start();
        setPageError(null);

        API.getEventTerms(
            queryParamProps.cid,
            queryParamProps.eid,
            queryParamProps.code
        )
            .then(data => {
                setEventData(data);
            })
            .catch(error => {
                setPageError(error.message);
            })
            .finally(() => {
                NProgress.done();

                //debug
                // setEventData({
                //     email: "zera.hamid@student.uts.edu.au",
                //     event_name: "Live at underground",
                //     event_tnc:
                //         "This event is only open to UTS students & UTS college students, unless stated otherwise.",
                // });
            });
    };

    const handleFormSubmit = () => {
        NProgress.start();
        localActions.request();

        API.agreeEventTerms(
            queryParamProps.cid,
            queryParamProps.eid,
            queryParamProps.code
        )
            .then(data => {
                localActions.success();
                setTimeout(() => {
                    setAgreeSuccess(true);
                    successRef.current.scrollIntoView(false);
                }, 1000);
            })
            .catch(error => {
                localActions.fail(error.message);
            })
            .finally(() => {
                NProgress.done();
            });
    };

    return (
        <React.Fragment>
            <Loader
                active={!eventData && !pageError}
                fixed={true}
                solid={true}
            ></Loader>

            <HeroContent
                heading={
                    eventData
                        ? `Terms & Conditions for ${eventData.event_name}`
                        : "Terms & Conditions"
                }
                description={
                    "In order to attend the event, please confirm the terms & conditions."
                }
                size="small"
            />

            <div className="component-builder container-fluid">
                <div className="page-content page-content--simple">
                    <div className="component-builder__component__inner col-xs-12 col-md-10 col-lg-8 col-md-offset-1 col-lg-offset-2">
                        <ServerErrorMessage>{pageError}</ServerErrorMessage>

                        {agreeSuccess ? (
                            // agreement success
                            <div ref={successRef} className="success">
                                <h2 className="heading-2 mb-4">
                                    Thanks for your submission.
                                </h2>

                                <p>
                                    You have successfully submitted the Terms &
                                    conditions agreement. See you at the event!
                                </p>

                                <br />

                                <div className="row">
                                    <div className="col-xs-12 col-sm-6">
                                        <Button to="/" styles={["green"]}>
                                            Go to homepage
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        ) : eventData ? (
                            // event terms content and checkbox
                            <>
                                <h2 className="heading-2 mb-4">
                                    Terms & Conditions
                                </h2>

                                <RichTextContent html={eventData.event_tnc} />

                                <br />
                                <br />

                                <ReactForm
                                    className={`terms-form ${
                                        state.error ? "is-invalid" : ""
                                    }`}
                                    onSuccess={handleFormSubmit}
                                    disableSubmitUntilValid
                                >
                                    <div className="form-inset form-inset--fluid">
                                        <h4 className="heading-4 mb-4 hyphen">
                                            {eventData.email}
                                        </h4>

                                        <ReactFormField
                                            type="checkbox"
                                            label="I confirm that I agree to the terms and conditions above"
                                            name="agree"
                                            required={true}
                                        />

                                        <div className="row">
                                            <div className="col-xs-12 col-sm-6">
                                                <Button
                                                    type="submit"
                                                    styles={["green"]}
                                                    aria-label="Submit"
                                                    loading={state.loading}
                                                    success={!!state.success}
                                                >
                                                    Submit
                                                </Button>
                                            </div>
                                        </div>

                                        <ServerErrorMessage>
                                            {state.error}
                                        </ServerErrorMessage>
                                    </div>
                                </ReactForm>
                            </>
                        ) : null}

                        <br />
                        <br />
                        <br />
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

EventTerms.defaultProps = {
    heroTitleSize: "heading-1",
    showRelated: true,
};

export default withRouter(EventTerms);
